/**
 * @file index.tsx
 *
 * @fileoverview Renders a date type field on
 * an entity as a date popup input.
 */
import { EntityField } from '~/jobiqo/models/entity';
import React, { useContext, useEffect } from 'react';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';
import { deriveDateFormat } from '~/jobiqo/utils/dates';
import { AppContextInterface } from '~/jobiqo/models/app';
import { AppContext } from '~/jobiqo/context/app';
import { extractInitialDate } from '~/jobiqo/components/Form/Widgets/Date/utils';
import { Controller, useFormContext } from 'react-hook-form';
import { SingleDatePopup } from '~/vcl/atoms/01-forms/06-date/single';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { EntityFieldRules } from '~/jobiqo/models/config';
import { useFieldRules } from '../../../hooks/useFieldRules';

// Props used by this component.
interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
}

const DateSingleWidget = ({
  entityField: { required, removeDay, description, label, id },
  fieldRules = {},
}: Props) => {
  const intl = useIntl();
  const { locale }: AppContextInterface = useContext(AppContext);
  const { control, getValues, setValue } = useFormContext();
  const values = getValues();
  const { hidden, required: ruleRequired } = useFieldRules(
    id,
    values,
    fieldRules,
  );

  const dateFormat: string = deriveDateFormat(intl, removeDay);

  // When field is hidden reset field state. this needs to be done inside a hook
  // otherwise it will not work.
  useEffect(() => {
    if (hidden && !!values[id]) {
      setValue(id, null);
    }
  }, [hidden, values, id]);

  if (hidden) {
    return null;
  }

  return (
    <Controller
      name={id}
      control={control}
      render={({ field: { value, name }, fieldState: { error } }) => (
        <>
          <SingleDatePopup
            id={name}
            label={label}
            description={description}
            required={required || ruleRequired}
            showMonthYearPicker={removeDay}
            placeholder={dateFormat.toLowerCase()}
            initialDate={extractInitialDate(value)}
            onDateChange={(date) => {
              // Format to what drupal wants.
              setValue(name, date ? format(date, 'yyyy-MM-dd') : null, {
                shouldValidate: true,
              });
            }}
            displayFormat={dateFormat}
            error={!!error}
            locale={locale}
          />
          {!!error && <InlineError message={error.message} />}
        </>
      )}
    />
  );
};

export default DateSingleWidget;
